export const DATA_VIEW_BODY_ATTRIBUTE = 'data-data-view-body';
export const DATA_VIEW_BODY_CONTENT_ATTRIBUTE = 'data-data-view-body-content';
export const DATA_VIEW_BODY_INTRINSIC_ATTRIBUTE =
  'data-data-view-body-intrinsic';
export const DATA_VIEW_BODY_RENDER_CONTENT_ATTRIBUTE =
  'data-data-view-body-render-content';
export const DATA_VIEW_BODY_RENDER_ROOT_ATTRIBUTE =
  'data-data-view-body-render-root';
export const DATA_VIEW_BODY_STATE_ATTRIBUTE = 'data-data-view-body-state';

export type DataViewBodyState = 'data' | 'empty' | 'setup';

export interface DataViewBodyContractMetrics {
  readonly hasVisibleRenderContent: boolean;
  readonly hasVisibleRenderRoot: boolean;
  readonly intrinsicHeight: number;
  readonly renderContentHeight: number;
  readonly renderHeight: number;
}

export interface DataViewBodyContractElementRect {
  readonly bottom: number;
  readonly height: number;
  readonly left: number;
  readonly top: number;
  readonly width: number;
}

export interface DataViewBodyContractElementDiagnostics {
  readonly childElementCount: number;
  readonly className: string;
  readonly display: string;
  readonly opacity: string;
  readonly path: string;
  readonly rect: DataViewBodyContractElementRect;
  readonly scrollHeight: number;
  readonly tagName: string;
  readonly visibility: string;
}

export interface DataViewBodyContractDiagnostics {
  readonly body: DataViewBodyContractElementDiagnostics | undefined;
  readonly content: readonly DataViewBodyContractElementDiagnostics[];
  readonly counts: {
    readonly content: number;
    readonly intrinsic: number;
    readonly renderContent: number;
    readonly renderRoot: number;
  };
  readonly host: DataViewBodyContractElementDiagnostics | undefined;
  readonly intrinsic: readonly DataViewBodyContractElementDiagnostics[];
  readonly renderContent: readonly DataViewBodyContractElementDiagnostics[];
  readonly renderRoot: readonly DataViewBodyContractElementDiagnostics[];
}

export type DataViewBodyContractViolationReason =
  | 'body-overflows-host'
  | 'missing-body'
  | 'missing-render-content'
  | 'missing-render-root';

export interface DataViewBodyContractViolation {
  readonly bodyState: DataViewBodyState | undefined;
  readonly reason: DataViewBodyContractViolationReason;
}

export interface DataViewBodyContractCheck {
  readonly bodyState: DataViewBodyState | undefined;
  readonly diagnostics: DataViewBodyContractDiagnostics;
  readonly metrics: DataViewBodyContractMetrics;
  readonly violation: DataViewBodyContractViolation | undefined;
}

export function dataViewBodyStateValue(
  state: DataViewBodyState
): DataViewBodyState {
  return state;
}

export function getDataViewBodyState(
  body: HTMLElement | null | undefined
): DataViewBodyState | undefined {
  const state = body?.getAttribute(DATA_VIEW_BODY_STATE_ATTRIBUTE);
  return state === 'data' || state === 'empty' || state === 'setup'
    ? state
    : undefined;
}

export function getDataViewBodyElement(
  host: ParentNode | null | undefined
): HTMLElement | undefined {
  if (!host) {
    return undefined;
  }
  return (
    Array.from(host.children).find(
      (child): child is HTMLElement =>
        child instanceof HTMLElement &&
        child.getAttribute(DATA_VIEW_BODY_ATTRIBUTE) === 'true'
    ) ?? undefined
  );
}

export function getDataViewBodyContentElements(
  body: HTMLElement | null | undefined
): HTMLElement[] {
  if (!body) {
    return [];
  }
  return Array.from(
    body.querySelectorAll<HTMLElement>(
      `[${DATA_VIEW_BODY_CONTENT_ATTRIBUTE}="true"]`
    )
  );
}

export function getDataViewBodyIntrinsicElements(
  body: HTMLElement | null | undefined
): HTMLElement[] {
  if (!body) {
    return [];
  }
  return Array.from(
    body.querySelectorAll<HTMLElement>(
      `[${DATA_VIEW_BODY_INTRINSIC_ATTRIBUTE}="true"]`
    )
  );
}

export function getDataViewBodyRenderContentElements(
  body: HTMLElement | null | undefined
): HTMLElement[] {
  if (!body) {
    return [];
  }
  return Array.from(
    body.querySelectorAll<HTMLElement>(
      `[${DATA_VIEW_BODY_RENDER_CONTENT_ATTRIBUTE}="true"]`
    )
  );
}

export function getDataViewBodyRenderRootElements(
  body: HTMLElement | null | undefined
): HTMLElement[] {
  if (!body) {
    return [];
  }
  return Array.from(
    body.querySelectorAll<HTMLElement>(
      `[${DATA_VIEW_BODY_RENDER_ROOT_ATTRIBUTE}="true"]`
    )
  );
}

function roundContractNumber(value: number): number {
  return Math.round(value * 100) / 100;
}

function getContractElementPath(element: HTMLElement): string {
  const parts: string[] = [];
  let current: HTMLElement | null = element;
  while (current && parts.length < 5) {
    const markers = [
      DATA_VIEW_BODY_ATTRIBUTE,
      DATA_VIEW_BODY_CONTENT_ATTRIBUTE,
      DATA_VIEW_BODY_INTRINSIC_ATTRIBUTE,
      DATA_VIEW_BODY_RENDER_ROOT_ATTRIBUTE,
      DATA_VIEW_BODY_RENDER_CONTENT_ATTRIBUTE,
      DATA_VIEW_BODY_STATE_ATTRIBUTE,
    ]
      .map(attribute => {
        const value = current?.getAttribute(attribute);
        return value == null ? '' : `[${attribute}="${value}"]`;
      })
      .filter(Boolean)
      .join('');
    const className =
      typeof current.className === 'string' && current.className
        ? `.${current.className.split(/\s+/).filter(Boolean).slice(0, 2).join('.')}`
        : '';
    parts.unshift(`${current.localName}${markers}${className}`);
    current = current.parentElement;
  }
  return parts.join(' > ');
}

function describeDataViewBodyContractElement(
  element: HTMLElement
): DataViewBodyContractElementDiagnostics {
  const rect = element.getBoundingClientRect();
  const computedStyle = getComputedStyle(element);
  return {
    childElementCount: element.childElementCount,
    className: typeof element.className === 'string' ? element.className : '',
    display: computedStyle.display,
    opacity: computedStyle.opacity,
    path: getContractElementPath(element),
    rect: {
      bottom: roundContractNumber(rect.bottom),
      height: roundContractNumber(rect.height),
      left: roundContractNumber(rect.left),
      top: roundContractNumber(rect.top),
      width: roundContractNumber(rect.width),
    },
    scrollHeight: element.scrollHeight,
    tagName: element.localName,
    visibility: computedStyle.visibility,
  };
}

export function getDataViewBodyContractDiagnostics(
  body: HTMLElement | null | undefined,
  host?: HTMLElement | null
): DataViewBodyContractDiagnostics {
  const content = getDataViewBodyContentElements(body);
  const intrinsic = getDataViewBodyIntrinsicElements(body);
  const renderContent = getDataViewBodyRenderContentElements(body);
  const renderRoot = getDataViewBodyRenderRootElements(body);
  return {
    body: body ? describeDataViewBodyContractElement(body) : undefined,
    content: content.map(describeDataViewBodyContractElement),
    counts: {
      content: content.length,
      intrinsic: intrinsic.length,
      renderContent: renderContent.length,
      renderRoot: renderRoot.length,
    },
    host: host ? describeDataViewBodyContractElement(host) : undefined,
    intrinsic: intrinsic.map(describeDataViewBodyContractElement),
    renderContent: renderContent.map(describeDataViewBodyContractElement),
    renderRoot: renderRoot.map(describeDataViewBodyContractElement),
  };
}

export function collectDataViewBodyResizeTargets({
  body,
  extraTargets = [],
  fallback,
  observeBodyChildren = true,
}: {
  body?: HTMLElement | null;
  extraTargets?: readonly (HTMLElement | null | undefined)[];
  fallback: HTMLElement;
  observeBodyChildren?: boolean;
}): HTMLElement[] {
  const targets: HTMLElement[] = [];
  const addTarget = (target: HTMLElement | null | undefined) => {
    if (!target || targets.includes(target)) {
      return;
    }
    targets.push(target);
  };

  addTarget(body ?? fallback);
  if (body && observeBodyChildren) {
    for (const child of Array.from(body.children)) {
      if (child instanceof HTMLElement) {
        addTarget(child);
      }
    }
    for (const content of getDataViewBodyContentElements(body)) {
      addTarget(content);
    }
    for (const intrinsic of getDataViewBodyIntrinsicElements(body)) {
      addTarget(intrinsic);
    }
    for (const renderContent of getDataViewBodyRenderContentElements(body)) {
      addTarget(renderContent);
    }
    for (const renderRoot of getDataViewBodyRenderRootElements(body)) {
      addTarget(renderRoot);
    }
  }
  for (const target of extraTargets) {
    addTarget(target);
  }
  return targets;
}

export function measureDataViewChromeHeight({
  body,
  host,
  ignore,
  visualScale,
}: {
  body?: HTMLElement | null;
  host: ParentNode;
  ignore?: (element: HTMLElement) => boolean;
  visualScale: number;
}): number {
  const scale = visualScale || 1;
  let chromeHeight = 0;
  for (const child of Array.from(host.children)) {
    if (child === body || !(child instanceof HTMLElement)) {
      continue;
    }
    if (ignore?.(child)) {
      continue;
    }
    chromeHeight += child.getBoundingClientRect().height / scale;
  }
  return chromeHeight;
}

export function measureDataViewBodyIntrinsicHeight({
  body,
  visualScale,
}: {
  body: HTMLElement | null | undefined;
  visualScale: number;
}): number {
  return measureDataViewBodyContractMetrics({
    body,
    visualScale,
  }).intrinsicHeight;
}

export function checkDataViewBodyContract({
  body,
  bodyState = getDataViewBodyState(body),
  host,
  visualScale,
}: {
  body: HTMLElement | null | undefined;
  bodyState?: DataViewBodyState;
  host?: HTMLElement | null;
  visualScale: number;
}): DataViewBodyContractCheck {
  const metrics = measureDataViewBodyContractMetrics({
    body,
    visualScale,
  });
  return {
    bodyState,
    diagnostics: getDataViewBodyContractDiagnostics(body, host),
    metrics,
    violation: getDataViewBodyContractViolation({
      body,
      bodyState,
      host,
      metrics,
    }),
  };
}

export function measureDataViewBodyContractMetrics({
  body,
  visualScale,
}: {
  body: HTMLElement | null | undefined;
  visualScale: number;
}): DataViewBodyContractMetrics {
  if (!body) {
    return {
      hasVisibleRenderContent: false,
      hasVisibleRenderRoot: false,
      intrinsicHeight: 0,
      renderContentHeight: 0,
      renderHeight: 0,
    };
  }

  const scale = visualScale || 1;
  const bodyRect = body.getBoundingClientRect();
  let contentBottom = 0;
  const measureElementBottom = (element: HTMLElement): number => {
    const rect = element.getBoundingClientRect();
    if (rect.height <= 0) {
      return 0;
    }
    const computedStyle = getComputedStyle(element);
    const marginBottom = Number.parseFloat(computedStyle.marginBottom) || 0;
    return (rect.bottom - bodyRect.top) / scale + marginBottom;
  };
  const measureContentElement = (element: HTMLElement): number => {
    const elementRect = element.getBoundingClientRect();
    const contentTop = Math.max(0, (elementRect.top - bodyRect.top) / scale);
    const overflowHeight =
      element.scrollHeight > element.clientHeight + 1
        ? element.scrollHeight / scale
        : 0;
    let measuredBottom = overflowHeight ? contentTop + overflowHeight : 0;
    let measuredChild = false;
    for (const child of Array.from(element.children)) {
      if (!(child instanceof HTMLElement)) {
        continue;
      }
      measuredChild = true;
      measuredBottom = Math.max(measuredBottom, measureElementBottom(child));
    }
    if (!measuredChild) {
      measuredBottom = Math.max(measuredBottom, measureElementBottom(element));
    }
    return measuredBottom;
  };
  const measureElements = (elements: readonly HTMLElement[]): number => {
    let bottom = 0;
    for (const element of elements) {
      bottom = Math.max(bottom, measureContentElement(element));
    }
    return bottom;
  };
  const isVisibleContractElement = (element: HTMLElement): boolean => {
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return false;
    }
    const computedStyle = getComputedStyle(element);
    return (
      computedStyle.display !== 'none' &&
      computedStyle.visibility !== 'hidden' &&
      computedStyle.opacity !== '0'
    );
  };

  const intrinsicElements = getDataViewBodyIntrinsicElements(body);
  const renderContentElements = getDataViewBodyRenderContentElements(body);
  const renderRootElements = getDataViewBodyRenderRootElements(body);
  const hasVisibleRenderContent = renderContentElements.some(
    isVisibleContractElement
  );
  const hasVisibleRenderRoot = renderRootElements.some(
    renderRoot =>
      isVisibleContractElement(renderRoot) ||
      renderContentElements.some(
        renderContent =>
          renderRoot.contains(renderContent) &&
          isVisibleContractElement(renderContent)
      )
  );
  const contentElements = intrinsicElements.length
    ? intrinsicElements
    : renderRootElements.length
      ? renderRootElements
      : getDataViewBodyContentElements(body);
  const renderContentHeight = measureElements(renderContentElements);
  const renderHeight = measureElements(renderRootElements);
  if (contentElements.length) {
    contentBottom = measureElements(contentElements);
    return {
      hasVisibleRenderContent,
      hasVisibleRenderRoot,
      intrinsicHeight: contentBottom,
      renderContentHeight,
      renderHeight,
    };
  }

  for (const child of Array.from(body.children)) {
    if (child instanceof HTMLElement) {
      contentBottom = Math.max(contentBottom, measureElementBottom(child));
    }
  }
  return {
    hasVisibleRenderContent,
    hasVisibleRenderRoot,
    intrinsicHeight: contentBottom,
    renderContentHeight,
    renderHeight,
  };
}

export function getDataViewBodyContractViolation({
  body,
  bodyState,
  host,
  metrics,
}: {
  body: HTMLElement | null | undefined;
  bodyState: DataViewBodyState | undefined;
  host?: HTMLElement | null;
  metrics: DataViewBodyContractMetrics;
}): DataViewBodyContractViolation | undefined {
  if (!bodyState) {
    return;
  }
  if (!body) {
    return {
      bodyState,
      reason: 'missing-body',
    };
  }
  if (!metrics.hasVisibleRenderRoot) {
    return {
      bodyState,
      reason: 'missing-render-root',
    };
  }
  if (!metrics.hasVisibleRenderContent) {
    return {
      bodyState,
      reason: 'missing-render-content',
    };
  }
  if (host && body) {
    const hostRect = host.getBoundingClientRect();
    const bodyRect = body.getBoundingClientRect();
    if (
      bodyRect.width > 0 &&
      bodyRect.height > 0 &&
      (hostRect.height <= 0 ||
        bodyRect.bottom > hostRect.bottom + 1 ||
        bodyRect.height > hostRect.height + 1)
    ) {
      return {
        bodyState,
        reason: 'body-overflows-host',
      };
    }
  }
  return;
}
